import React from 'react';
import Tooltip, { DefaultTooltipPopperProps } from 'js/components/elements/mui/Tooltip';
import { formatInTimeZone } from 'date-fns-tz';
import styles from './FeedConfigurationTooltip.module.scss';

interface FeedConfigurationData {
  label: string;
  codeName: string;
  syncMode: string;
  primaryKeys?: string | string[];
  forceResync: boolean;
  fieldSelection: string;
  selectedFields?: string;
  fieldMappings?: Record<string, string>;
  totalAvailableFields?: number;
  lastIngested?: string;
  detected?: string;
}

interface FeedConfigurationTooltipProps {
  feedData: FeedConfigurationData;
}

const FeedConfigurationTooltipContent = ({ feedData }: FeedConfigurationTooltipProps) => {
  const getUserTimezone = () => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (error) {
      return 'UTC'; // fallback
    }
  };

  const formatLastIngested = (timestamp?: string) => {
    if (!timestamp) return '';
    try {
      const userTimezone = getUserTimezone();
      return formatInTimeZone(new Date(timestamp), userTimezone, 'MM/dd/yyyy, hh:mm:ss a');
    } catch (error) {
      // Fallback to basic formatting if timezone conversion fails
      return formatInTimeZone(new Date(timestamp), 'UTC', 'MM/dd/yyyy, hh:mm:ss a');
    }
  };

  const formatLastDetected = (timestamp?: string) => {
    if (!timestamp) return '';
    try {
      const userTimezone = getUserTimezone();
      return formatInTimeZone(new Date(timestamp), userTimezone, 'MM/dd/yyyy, hh:mm:ss a');
    } catch (error) {
      // Fallback to basic formatting if timezone conversion fails
      return formatInTimeZone(new Date(timestamp), 'UTC', 'MM/dd/yyyy, hh:mm:ss a');
    }
  };

  return (
    <div className={styles.tooltipContent}>
      <div className={styles.tooltipItem}>
        <b>Feed:</b> {feedData.label}
      </div>

      <div className={styles.tooltipItem}>
        <b>Code Name:</b> {feedData.codeName}
      </div>
      <div className={styles.tooltipItem}>
        <b>Sync Mode:</b> {feedData.syncMode}
      </div>

      {feedData.primaryKeys && (
        <div className={styles.tooltipItem}>
          <b>Primary Keys:</b>{' '}
          {Array.isArray(feedData.primaryKeys)
            ? feedData.primaryKeys.join(', ')
            : feedData.primaryKeys}
        </div>
      )}

      <div className={styles.tooltipItem}>
        <b>Force Resync:</b> {feedData.forceResync ? 'Yes' : 'No'}
      </div>

      <div className={styles.tooltipItem}>
        <b>Fields:</b> {feedData.fieldSelection}{' '}
        {feedData.totalAvailableFields ? ' of ' + feedData.totalAvailableFields : ''}
      </div>

      {feedData.selectedFields && (
        <div className={styles.tooltipItem}>
          <b>Selected Fields:</b>
          <div className={styles.fieldList}>
            {feedData.selectedFields.split(',').map((field, index) => (
              <div key={index} className={styles.fieldItem}>
                {field.trim()}
              </div>
            ))}
          </div>
        </div>
      )}

      {feedData.lastIngested && (
        <div className={styles.tooltipItem}>
          <b>Last Ingested:</b> {formatLastIngested(feedData.lastIngested)}
        </div>
      )}

      {feedData.detected && (
        <div className={styles.tooltipItem}>
          <b>Detected:</b> {formatLastDetected(feedData.detected)}
        </div>
      )}

      <div className={styles.tooltipFooter}>
        <small>All times in {getUserTimezone()}</small>
      </div>
    </div>
  );
};

interface FeedConfigurationTooltipProps {
  feedData: FeedConfigurationData;
  children: React.ReactElement;
}

export const FeedConfigurationTooltip = ({ feedData, children }: FeedConfigurationTooltipProps) => {
  const tooltipContent = <FeedConfigurationTooltipContent feedData={feedData} />;

  return (
    <Tooltip tooltipTheme="fieldInfo" title={tooltipContent}>
      {children}
    </Tooltip>
  );
};
